import React, { useEffect, useState } from 'react';
import { Card, Table, Row, Col, Tag, Typography } from 'antd';
import { useOwnerSupplyChain } from '@/hooks/useOwnerIntelligence';
import { useCurrency } from '@/contexts/CurrencyContext';
import { PrimaryKpiCard, OiPageFrame, OiDetailDrawer, OiSupplierPanel } from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { OiRiskHeatmap } from '@/components/owner-intelligence/charts/OiCharts';
import api from '@/services/api.service';

const { Text } = Typography;

function unwrap<T>(res: unknown): T {
  const data = (res as { data?: unknown })?.data ?? res;
  return ((data as { data?: T })?.data ?? data) as T;
}

const SupplyChainIntelligencePage: React.FC = () => {
  const { data, isLoading, error } = useOwnerSupplyChain();
  const { formatCurrency } = useCurrency();
  const [drawerRow, setDrawerRow] = useState<Record<string, unknown> | null>(null);
  const [portfolio, setPortfolio] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    api.get('/owner-intelligence/supplier-portfolio')
      .then((res) => setPortfolio(unwrap(res)))
      .catch(() => setPortfolio(null));
  }, []);

  const suppliers = data?.supplier_performance ?? [];
  const portfolioHeat = (portfolio?.risk_heatmap ?? []) as Array<{ supplier_name?: string; score?: number; risk_tier?: string }>;
  const heatCells = (portfolioHeat.length ? portfolioHeat : suppliers).map((s, i) => ({
    x: Math.min(5, (i % 5) + 1),
    y: Math.min(5, Math.max(1, Math.ceil((Number((s as { score?: number }).score ?? 50)) / 20))),
    count: 1,
  }));

  const segmentPortfolio = (portfolio?.strategic_portfolio ?? {}) as Record<string, { count: number }>;
  const concentration = portfolio?.procurement_concentration as { top3_share_pct?: number; total_spend_12m?: number } | undefined;
  const improvementPrograms = (portfolio?.improvement_programs ?? []) as unknown[];

  return (
    <OiPageFrame
      subtitle="Supplier performance, PO commitments, procurement concentration, and supplier portfolio."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="Supply chain intelligence unavailable"
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <PrimaryKpiCard label="Open PO value" value={formatCurrency(Number(data?.open_po?.totalValue ?? 0))} />
        </Col>
        <Col span={6}>
          <PrimaryKpiCard label="Low stock SKUs" value={data?.low_stock_skus ?? 0} />
        </Col>
        <Col span={6}>
          <PrimaryKpiCard
            label="Overdue POs"
            value={data?.action_summary?.overdue_po_count ?? data?.action_summary?.overdue_pos?.length ?? 0}
          />
        </Col>
        <Col span={6}>
          <PrimaryKpiCard
            label="Top-3 spend concentration"
            value={concentration?.top3_share_pct != null ? `${concentration.top3_share_pct}%` : '—'}
          />
        </Col>
      </Row>

      <Card title="Strategic supplier portfolio" style={{ marginBottom: 16 }} bordered={false}>
        <Row gutter={8}>
          {Object.entries(segmentPortfolio).map(([seg, info]) => (
            <Col key={seg}><Tag>{seg}: {info.count}</Tag></Col>
          ))}
          {!Object.keys(segmentPortfolio).length ? <Text type="secondary">Run Procurement Control Tower score refresh to populate segments.</Text> : null}
        </Row>
      </Card>

      <Card title="Supplier risk heatmap" style={{ marginBottom: 16 }} bordered={false}>
        <OiRiskHeatmap cells={heatCells} />
      </Card>

      <Card title="Supplier performance (delayed POs)" style={{ marginBottom: 16 }} bordered={false}>
        <Table
          size="small"
          rowKey="supplier_name"
          dataSource={suppliers}
          onRow={(row) => ({
            onClick: () => setDrawerRow(row as unknown as Record<string, unknown>),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: 'Supplier', dataIndex: 'supplier_name' },
            { title: 'Overdue POs', dataIndex: 'overdue_count' },
            { title: 'Open value', dataIndex: 'open_value', render: (v: number) => formatCurrency(v) },
          ]}
        />
      </Card>

      <Card title="Active improvement programs" style={{ marginBottom: 16 }} bordered={false}>
        <Table
          size="small"
          rowKey="id"
          dataSource={improvementPrograms as object[]}
          columns={[
            { title: 'Supplier', dataIndex: 'supplier_name' },
            { title: 'Plan', dataIndex: 'title' },
            { title: 'Problem', dataIndex: 'problem_statement', ellipsis: true },
            { title: 'Status', dataIndex: 'status' },
          ]}
          locale={{ emptyText: 'No active supplier development plans' }}
        />
      </Card>

      <Card title="Inventory by warehouse" bordered={false}>
        <Table
          size="small"
          rowKey="warehouse_name"
          dataSource={data?.inventory_by_warehouse ?? []}
          columns={[
            { title: 'Warehouse', dataIndex: 'warehouse_name' },
            { title: 'Value', dataIndex: 'stock_value', render: (v: number) => formatCurrency(v) },
          ]}
        />
      </Card>

      <OiDetailDrawer open={Boolean(drawerRow)} onClose={() => setDrawerRow(null)} title="Supplier detail">
        {drawerRow ? <OiSupplierPanel row={drawerRow} /> : null}
      </OiDetailDrawer>
    </OiPageFrame>
  );
};

export default SupplyChainIntelligencePage;
